import { AppstoreAddOutlined } from "@ant-design/icons"
import { Flex, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  setReportFilters,
  usedSpecKeys
} from "../../helpers/reportDefinition.ts"
import { useReportBuildActions } from "../../hooks/useReportBuildActions.ts"
import { reportCanvasApplyDefinition } from "../../redux/reportCanvas.slice.ts"
import type { ReportWidgetFilters } from "../../types/canvas.types.ts"
import { ReportCanvas } from "../canvas/ReportCanvas.tsx"
import { ReportFilterBar } from "../canvas/ReportFilterBar.tsx"
import { ReportAddWidgetPanel } from "./ReportAddWidgetPanel.tsx"
import { ReportWidgetSettingsPanel } from "./ReportWidgetSettingsPanel.tsx"

const { Text } = Typography

export const ReportBuildLayout = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const catalog = useAppSelector((s) => s.reportCanvas.catalog)
  const catalogStatus = useAppSelector((s) => s.reportCanvas.catalogStatus)
  const { addWidget } = useReportBuildActions()

  const addedSpecKeys = useMemo(() => usedSpecKeys(definition), [definition])

  const handleReportFiltersChange = (filters: ReportWidgetFilters) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportFilters(definition, filters)))
  }

  return (
    <Flex gap="middle" className="h-full" align="stretch">
      <ReportAddWidgetPanel
        catalog={catalog}
        loading={catalogStatus === "loading"}
        onAdd={addWidget}
        addedSpecKeys={addedSpecKeys}
      />

      <Flex vertical gap="middle" className="min-w-0 flex-1 overflow-auto">
        <Text
          type="secondary"
          style={{ fontSize: 12, fontWeight: 600 }}
        >
          {t("reports.build.globalFilters")}
        </Text>
        <ReportFilterBar
          filters={definition?.filters ?? {}}
          onChange={handleReportFiltersChange}
        />

        <ReportCanvas selectable hidePanels />

        <Flex
          align="center"
          justify="center"
          gap="small"
          style={{
            border: `1px dashed ${neutral.border}`,
            borderRadius: 12,
            padding: "18px 12px",
            color: neutral.muted
          }}
        >
          <AppstoreAddOutlined />
          <Text type="secondary">{t("reports.build.dropzone")}</Text>
        </Flex>
      </Flex>

      <ReportWidgetSettingsPanel />
    </Flex>
  )
}
